import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { ArrowLeft, ArrowRight, Check, Clock, Lock, X } from "lucide-react";
import { styles, ErrorBox, Field, PageShell, ThanksScreen, cleanText, flagField, toDigits } from "../components/wizard";
import { loadSurvey, sendSurvey, surveyDate, surveyMinutes, surveyText } from "../components/surveyData";

const ParticipationTermsModal = lazy(() => import("../components/ParticipationTermsModal"));

// Sondage (jawdaplus-prototype-v5-fr.html): one question per card, a progress bar under the header.
// One answer and yes / no go to the next question by themselves; several answers and free text have
// a "Suivant" button. With the Tombola, a phone number at the end gives a participation number.

const TEXT_MAX = 500;

// lucide clipboard with a check and a line (prototype icon), also on the home card
export const SurveyIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect width="8" height="4" x="8" y="2" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="m9 12 1.5 1.5L13 11" />
    <path d="M9 17h6" />
  </svg>
);

// lucide ticket with a star (prototype icon of the Tombola)
export const TicketIcon = ({ size = 24, strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M15 5v2" />
    <path d="M15 11v2" />
    <path d="M15 17v2" />
    <path d="m8.5 9.2.9 1.8 2 .3-1.45 1.4.35 2-1.8-.95-1.8.95.35-2L5.6 11.3l2-.3z" fill="currentColor" strokeWidth="1" />
  </svg>
);

const isAnswered = (question, answer) => {
  if (question.type === "qcs") return typeof answer === "number";
  if (question.type === "qcm") return !!answer?.length;
  if (question.type === "yn") return answer === "yes" || answer === "no";
  return !!(answer && String(answer).trim());
};

// 10 digits (mobile) or 9 (landline), as in the prototype
const phoneOk = (phone) => /^0\d{8,9}$/.test(phone);

// The next card wipes in over the previous one (prototype "balayage"); the height follows the content.
const WIPE = 460;
const EASE = "cubic-bezier(.4,0,.2,1)";
const noMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function Stage({ stepKey, children }) {
  const stageRef = useRef(null);
  const ghostRef = useRef(null);
  const cardRef = useRef(null);
  const last = useRef(null);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const card = cardRef.current;
    const prev = last.current;
    if (prev && prev.key !== stepKey && !noMotion() && card.animate) {
      // the previous card, as it was last drawn, fades out in place
      const ghost = prev.clone;
      ghostRef.current.replaceChildren(ghost);
      ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: WIPE * 0.45, easing: "ease-out", fill: "forwards" }).onfinish = () =>
        ghost.remove();
      stage.animate([{ height: `${prev.height}px` }, { height: `${card.offsetHeight}px` }], { duration: WIPE, easing: EASE });
      card.animate(
        [
          { clipPath: "inset(0 100% 0 0 round 16px)", opacity: 0.5 },
          { clipPath: "inset(0 0 0 0 round 16px)", opacity: 1 },
        ],
        { duration: WIPE * 0.8, delay: WIPE * 0.2, easing: EASE, fill: "backwards" }
      );
      // scrolls only when the new card goes past the screen
      const r = card.getBoundingClientRect();
      if (r.bottom > window.innerHeight - 12 || r.top < 8) card.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [stepKey]);

  // after every drawing: the card to fade out at the next step
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (card) last.current = { key: stepKey, clone: card.cloneNode(true), height: card.offsetHeight };
  });

  return (
    <div ref={stageRef} className={styles.svStage} aria-live="polite">
      <div ref={ghostRef} className={styles.svGhost} aria-hidden="true" />
      <div ref={cardRef} className={styles.svCard}>
        {children}
      </div>
    </div>
  );
}

const Chip = ({ icon, children }) => (
  <span className={styles.svChip}>
    {icon}
    <span>{children}</span>
  </span>
);

function Prize({ survey }) {
  const { t, i18n } = useTranslation();
  return (
    <div className={styles.svPrize}>
      <span className={styles.svTk}>
        <TicketIcon />
      </span>
      <div>
        <b>{t("svPrizeTitle", { d: surveyDate(survey.draw, i18n.language) })}</b>
        <span>{surveyText(survey.prize, i18n.language)}</span>
      </div>
    </div>
  );
}

function Intro({ survey, onStart }) {
  const { t, i18n } = useTranslation();
  const n = survey.questions.length;
  const desc = surveyText(survey.desc, i18n.language);
  return (
    <>
      <div className={styles.svMeta}>
        <Chip icon={<SurveyIcon size={15} />}>{t("svChipQ", { n })}</Chip>
        <Chip icon={<Clock size={15} strokeWidth={2} aria-hidden />}>{t("svChipT", { m: surveyMinutes(n) })}</Chip>
        <Chip icon={<Lock size={15} strokeWidth={2} aria-hidden />}>{t("svChipAnon")}</Chip>
      </div>
      <h2 className={styles.svTitle}>{surveyText(survey.title, i18n.language)}</h2>
      {desc && <p className={styles.svDesc}>{desc}</p>}
      {survey.tombola && <Prize survey={survey} />}
      <button type="button" className={classNames(styles.next, styles.block)} onClick={onStart}>
        <span>{t("svStart")}</span>
        <ArrowRight size={18} strokeWidth={2.2} aria-hidden />
      </button>
    </>
  );
}

function QuestionCard({ index, count, question, answer, onAnswer, onPrev, onNext }) {
  const { t, i18n } = useTranslation();
  const [maxReached, setMaxReached] = useState(false);
  const required = question.req !== false;
  const answered = isAnswered(question, answer);
  const multi = question.type === "qcm";
  const help =
    question.type === "qcs"
      ? t("svHelpOne")
      : multi
      ? question.max
        ? t("svHelpMax", { k: question.max })
        : t("svHelpMany")
      : question.type === "yn"
      ? t("svHelpYn")
      : t("svHelpText");

  // one answer and yes / no: the next question comes by itself
  const pickAndGo = (value) => {
    onAnswer(value);
    onNext(300);
  };

  const toggle = (ix) => {
    const list = answer || [];
    if (list.includes(ix)) {
      setMaxReached(false);
      return onAnswer(list.filter((v) => v !== ix));
    }
    if (question.max && list.length >= question.max) return setMaxReached(true);
    onAnswer([...list, ix]);
  };

  // "Suivant" with several answers and free text, or when coming back to an answered question
  // (decided when the card opens: a tap on one answer moves on without showing it)
  const [withNext] = useState(multi || question.type === "text" || answered);

  return (
    <>
      <div className={styles.svCount}>{t("svOf", { i: index + 1, n: count })}</div>
      <h2 className={styles.svQ}>{surveyText(question.q, i18n.language)}</h2>
      <p className={styles.svHelp}>{required ? help : `${help} · ${t("svOptional")}`}</p>

      {(question.type === "qcs" || multi) && (
        <div className={styles.svOpts} role={multi ? "group" : "radiogroup"}>
          {(question.opts || []).map((opt, ix) => {
            const on = multi ? (answer || []).includes(ix) : answer === ix;
            return (
              <button
                type="button"
                key={ix}
                className={classNames(styles.svOpt, { [styles.multi]: multi, [styles.isOn]: on })}
                role={multi ? "checkbox" : "radio"}
                aria-checked={on}
                onClick={() => (multi ? toggle(ix) : pickAndGo(ix))}
              >
                <span className={styles.svMark}>
                  <Check size={15} strokeWidth={3} aria-hidden />
                </span>
                <span>{surveyText(opt, i18n.language)}</span>
              </button>
            );
          })}
        </div>
      )}
      {maxReached && <span className={classNames(styles.hint, styles.isBad)}>{t("svMaxReached", { k: question.max })}</span>}

      {question.type === "yn" && (
        <div className={styles.svYn} role="radiogroup">
          {[
            ["yes", t("svYes"), Check],
            ["no", t("svNo"), X],
          ].map(([value, label, Icon]) => (
            <button
              type="button"
              key={value}
              className={classNames(styles.svOpt, { [styles.isOn]: answer === value })}
              role="radio"
              aria-checked={answer === value}
              onClick={() => pickAndGo(value)}
            >
              <span className={styles.svIco}>
                <Icon size={24} strokeWidth={2.6} aria-hidden />
              </span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}

      {question.type === "text" && (
        <>
          <textarea
            className={styles.textarea}
            style={{ minHeight: 110 }}
            maxLength={TEXT_MAX}
            placeholder={t("svTextPh")}
            aria-label={surveyText(question.q, i18n.language)}
            value={answer || ""}
            onChange={(e) => onAnswer(cleanText(e.target.value))}
          />
          <div className={styles.count}>
            {(answer || "").length} / {TEXT_MAX}
          </div>
        </>
      )}

      <div className={styles.svNav}>
        <button type="button" className={styles.ghost} aria-label={t("svPrev")} onClick={onPrev}>
          <ArrowLeft size={18} strokeWidth={2.2} aria-hidden />
        </button>
        {withNext && (
          <button type="button" className={styles.next} disabled={required && !answered} onClick={() => onNext()}>
            <span>{t("svNext")}</span>
            <ArrowRight size={18} strokeWidth={2.2} aria-hidden />
          </button>
        )}
      </div>
      {!required && question.type !== "text" && (
        <button
          type="button"
          className={classNames(styles.svLink, styles.svSkip)}
          onClick={() => {
            onAnswer(null);
            onNext();
          }}
        >
          {t("svSkip")}
        </button>
      )}
    </>
  );
}

function LastCard({ survey, answers, onPrev, onSent }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [rulesOk, setRulesOk] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const fail = (key, field) => {
    setError(t(key));
    flagField(field);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError("");
    // without a number the answers are sent without taking part in the draw
    const withPhone = survey.tombola && phone.length > 0;
    if (withPhone && !phoneOk(phone)) return fail("svErrTel", "phone");
    if (withPhone && !rulesOk) return fail("svErrRules", "rules");
    setSending(true);
    try {
      onSent(await sendSurvey({ answers, phone: withPhone ? phone : null }));
    } catch (_) {
      setError(t("suggestionErrorServer"));
      setSending(false);
    }
  };

  return (
    <>
      <h2 className={styles.svTitle}>{t("svLastTitle")}</h2>
      {survey.tombola ? (
        <>
          <Prize survey={survey} />
          <p className={styles.svDesc}>{t("svLastTb")}</p>
          <Field label={t("svTelLabel")} optional field="phone">
            <input
              type="tel"
              className={classNames(styles.input, styles.ltr)}
              autoComplete="tel"
              inputMode="numeric"
              placeholder="0555123456"
              value={phone}
              onChange={(e) => setPhone(toDigits(e.target.value).slice(0, 10))}
            />
          </Field>
          <div className={styles.svRules} data-field="rules">
            <label className={styles.consentRow}>
              <input type="checkbox" checked={rulesOk} onChange={(e) => setRulesOk(e.target.checked)} />
              <span className={styles.consentText}>{t("svRules")}</span>
            </label>
            <button type="button" className={styles.svLink} onClick={() => setRulesOpen(true)}>
              {t("svRulesLink")}
            </button>
          </div>
        </>
      ) : (
        <p className={styles.svDesc}>{t("svLastNoTb")}</p>
      )}

      <form noValidate onSubmit={submit}>
        <ErrorBox>{error}</ErrorBox>
        <button type="submit" className={styles.submit} disabled={sending}>
          <span>{sending ? t("suggestionSending") : t("svSend")}</span>
        </button>
      </form>
      <p className={styles.svNote}>
        <Lock size={15} strokeWidth={2} aria-hidden />
        <span>{survey.tombola ? t("svPrivacy") : t("svPrivacyNoTb")}</span>
      </p>
      <button type="button" className={classNames(styles.svLink, styles.svSkip)} onClick={onPrev}>
        {t("svPrev")}
      </button>

      {/* the rules of the client's lottery (production modal of the contest) */}
      <Suspense fallback={null}>
        <ParticipationTermsModal
          isOpen={rulesOpen}
          onAccept={() => {
            setRulesOk(true);
            setRulesOpen(false);
          }}
          onRefuse={() => setRulesOpen(false)}
        />
      </Suspense>
    </>
  );
}

export default function SurveyForm({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(undefined); // undefined: loading, null: none in progress
  const [step, setStep] = useState(-1); // -1: intro, 0..n-1: questions, n: last step
  const [answers, setAnswers] = useState([]);
  const [sent, setSent] = useState(null); // { ticket }
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    let alive = true;
    loadSurvey()
      .then((s) => alive && setSurvey(s))
      .catch(() => alive && setSurvey(null));
    return () => {
      alive = false;
    };
  }, []);

  const count = survey?.questions.length || 0;
  const goTo = (i) => setStep(Math.max(-1, Math.min(count, i)));
  // `delay`: after an automatic answer, the choice shows a moment; nothing happens if the person went elsewhere
  const goNext = (from, delay) => {
    if (!delay) return goTo(from + 1);
    setTimeout(() => stepRef.current === from && goTo(from + 1), delay);
  };
  const setAnswer = (i, value) =>
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });

  const done = sent && (
    <ThanksScreen
      title={t("svOkTitle")}
      text={sent.ticket ? t("svOkTb", { d: surveyDate(survey.draw, i18n.language) }) : t("svOkText")}
      onHome={() => navigate("/")}
    >
      {sent.ticket && (
        <div className={styles.svTicket}>
          <span>{t("svTicketLabel")}</span>
          <b dir="ltr">{sent.ticket}</b>
        </div>
      )}
    </ThanksScreen>
  );

  const progress = step < 0 || !count ? 0 : Math.min(100, (step / count) * 100);

  let card;
  if (step < 0) card = <Intro survey={survey} onStart={() => goTo(0)} />;
  else if (step >= count) card = <LastCard survey={survey} answers={answers} onPrev={() => goTo(count - 1)} onSent={setSent} />;
  else
    card = (
      <QuestionCard
        key={step}
        index={step}
        count={count}
        question={survey.questions[step]}
        answer={answers[step]}
        onAnswer={(value) => setAnswer(step, value)}
        onPrev={() => goTo(step - 1)}
        onNext={(delay) => goNext(step, delay)}
      />
    );

  return (
    <PageShell title={t("svTitle")} onBack={() => navigate("/")} currentLang={currentLang} setCurrentLang={setCurrentLang} done={done}>
      <div className={styles.svBar} aria-hidden="true">
        <i style={{ width: `${progress}%` }} />
      </div>
      <div className={styles.cardBody}>
        {survey === undefined ? (
          <p className={styles.statusText}>{t("suggestionLoading")}</p>
        ) : survey === null ? (
          <div className={styles.svCard}>
            <p className={styles.svDesc} style={{ margin: 0 }}>
              {t("svNone")}
            </p>
          </div>
        ) : (
          <Stage stepKey={step}>{card}</Stage>
        )}
      </div>
    </PageShell>
  );
}
